import type { FC, MouseEvent } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import MoreVert from '@mui/icons-material/MoreVert';
import PreviewOutlined from '@mui/icons-material/PreviewOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import { useAnchorMenu } from 'hooks';

interface GoogleContentPreviewMoreMenuProps {
  previewUrl?: string;
  position?: 'top' | 'bottom';
  downloadProgress?: number | null;
  onOpenChange?: (open: boolean) => void;
  onCopyPreviewUrl: () => void;
  onDownloadPreview: () => void;
}

const iconBtnSx = { color: 'white', p: 0.75 } as const;

const GoogleContentPreviewMoreMenu: FC<GoogleContentPreviewMoreMenuProps> = ({
  previewUrl,
  position = 'bottom',
  downloadProgress = null,
  onOpenChange,
  onCopyPreviewUrl,
  onDownloadPreview,
}) => {
  const menu = useAnchorMenu();
  const isDownloading = downloadProgress !== null;

  const handleOpenMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    menu.handleOpenMenu(e);
    onOpenChange?.(true);
  };

  const handleCloseMenu = () => {
    menu.handleCloseMenu();
    onOpenChange?.(false);
  };

  const handleCopyPreviewUrl = () => {
    onCopyPreviewUrl();
    handleCloseMenu();
  };

  return (
    <>
      <IconButton onClick={handleOpenMenu} size="small" sx={iconBtnSx}>
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={menu.anchor}
        open={menu.isOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: position === 'bottom' ? 'top' : 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: position === 'bottom' ? 'bottom' : 'top', horizontal: 'right' }}
      >
        <MenuItem disabled={!previewUrl} onClick={handleCopyPreviewUrl}>
          <ListItemIcon>
            <PreviewOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy Preview URL</ListItemText>
        </MenuItem>
        <MenuItem disabled={!previewUrl || isDownloading} onClick={onDownloadPreview}>
          <ListItemIcon>
            <DownloadOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>{isDownloading ? 'Downloading...' : 'Download Preview'}</ListItemText>
          {isDownloading && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {downloadProgress}%
            </Typography>
          )}
        </MenuItem>
      </Menu>
    </>
  );
};

export default GoogleContentPreviewMoreMenu;
